import * as hbApp from '@app/hb-app'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import {
  IRequestOptions,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { Record } from 'immutable'
import * as LogRocket from 'logrocket'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum FeedbackType {
  Bug = 'bug',
  Enhancement = 'enhancement',
  Visual = 'visual',
}

export interface IFeedbackState {
  readonly feedbackText: string
  readonly feedbackType: FeedbackType
  readonly isOpen: boolean
  readonly isSaving: boolean
}

const FeedbackStateRecord = Record({
  feedbackText: '',
  feedbackType: FeedbackType.Bug,
  isOpen: false,
  isSaving: false,
})

export class FeedbackState extends FeedbackStateRecord
  implements IFeedbackState {
  public static fromJS(initialState?: Partial<IFeedbackState>) {
    return new FeedbackState(initialState)
  }

  public readonly feedbackText: string
  public readonly feedbackType: FeedbackType
  public readonly isOpen: boolean
  public readonly isSaving: boolean

  constructor(props?: Partial<FeedbackState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum FeedbackActionTypes {
  CloseDialog = 'FEEDBACK::CLOSE_DIALOG',
  SetFeedbackText = 'FEEDBACK::SET_FEEDBACK_TEXT',
  SetFeedbackType = 'FEEDBACK::SET_FEEDBACK_TYPE',
  ShowDialog = 'FEEDBACK::SHOW_DIALOG',
  SubmitFeedback = 'FEEDBACK::SUBMIT_FEEDBACK',
  SubmitFeedbackFailure = 'FEEDBACK::SUBMIT_FEEDBACK_FAILURE',
  SubmitFeedbackRequest = 'FEEDBACK::SUBMIT_FEEDBACK_REQUEST',
  SubmitFeedbackSuccess = 'FEEDBACK::SUBMIT_FEEDBACK_SUCCESS',
}

interface ICloseDialogAction {
  type: FeedbackActionTypes.CloseDialog
}

interface IShowDialogAction {
  type: FeedbackActionTypes.ShowDialog
}

interface ISetFeedbackTextAction {
  feedbackText: string
  type: FeedbackActionTypes.SetFeedbackText
}

interface ISetFeedbackTypeAction {
  feedbackType: FeedbackType
  type: FeedbackActionTypes.SetFeedbackType
}

interface ISubmitFeedbackAction {
  feedbackText: string
  feedbackType: FeedbackType
  type: FeedbackActionTypes.SubmitFeedback
}

interface ISubmitFeedbackFailureAction {
  type: FeedbackActionTypes.SubmitFeedbackFailure
}

interface ISubmitFeedbackRequestAction {
  type: FeedbackActionTypes.SubmitFeedbackRequest
}

interface ISubmitFeedbackSuccessAction {
  type: FeedbackActionTypes.SubmitFeedbackSuccess
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ICloseDialogAction
  | ISetFeedbackTextAction
  | ISetFeedbackTypeAction
  | IShowDialogAction
  | ISubmitFeedbackFailureAction
  | ISubmitFeedbackRequestAction
  | ISubmitFeedbackSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------

function closeDialog(): ICloseDialogAction {
  return { type: FeedbackActionTypes.CloseDialog }
}

function setFeedbackText(feedbackText: string): ISetFeedbackTextAction {
  return {
    feedbackText,
    type: FeedbackActionTypes.SetFeedbackText,
  }
}

function setFeedbackType(feedbackType: FeedbackType): ISetFeedbackTypeAction {
  return {
    feedbackType,
    type: FeedbackActionTypes.SetFeedbackType,
  }
}

function showDialog(): IShowDialogAction {
  return { type: FeedbackActionTypes.ShowDialog }
}

function submitFeedback(
  feedbackType: FeedbackType,
  feedbackText: string,
): ISubmitFeedbackAction {
  return {
    feedbackText,
    feedbackType,
    type: FeedbackActionTypes.SubmitFeedback,
  }
}

function submitFeedbackFailure(): ISubmitFeedbackFailureAction {
  return { type: FeedbackActionTypes.SubmitFeedbackFailure }
}

function submitFeedbackRequest(): ISubmitFeedbackRequestAction {
  return { type: FeedbackActionTypes.SubmitFeedbackRequest }
}

function submitFeedbackSuccess(): ISubmitFeedbackSuccessAction {
  return { type: FeedbackActionTypes.SubmitFeedbackSuccess }
}

export const actionCreators = {
  closeDialog,
  setFeedbackText,
  setFeedbackType,
  showDialog,
  submitFeedback,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<FeedbackState> = (
  state: FeedbackState = new FeedbackState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case FeedbackActionTypes.CloseDialog:
      return state.set('isOpen', false) as FeedbackState

    case FeedbackActionTypes.SetFeedbackText:
      return state.set('feedbackText', action.feedbackText) as FeedbackState

    case FeedbackActionTypes.SetFeedbackType:
      return state.set('feedbackType', action.feedbackType) as FeedbackState

    case FeedbackActionTypes.ShowDialog:
      return state.set('isOpen', true) as FeedbackState

    case FeedbackActionTypes.SubmitFeedbackFailure:
      return state.set('isSaving', false) as FeedbackState

    case FeedbackActionTypes.SubmitFeedbackRequest:
      return state.set('isSaving', true) as FeedbackState

    case FeedbackActionTypes.SubmitFeedbackSuccess:
      return state
        .set('feedbackText', '')
        .set('feedbackType', FeedbackType.Bug)
        .set('isSaving', false) as FeedbackState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* submitFeedbackSaga(action: ISubmitFeedbackAction) {
  try {
    yield put(submitFeedbackRequest())

    const opts: IRequestOptions = {
      body: JSON.stringify({
        commit: hbApp.commit,
        sessionUrl: LogRocket.sessionURL,
        text: action.feedbackText,
        type: action.feedbackType,
        url: window.location.href,
        version: hbApp.version,
      }),
      method: RequestMethod.POST,
    }

    const result = yield makeAuthenticatedRequest('/api/Feedback', opts)

    if (result.success === true) {
      yield put(closeDialog())
      yield put(submitFeedbackSuccess())
      yield put(successToast('Success', 'Feedback submitted. Thank you!'))
    } else {
      yield put(submitFeedbackFailure())
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(submitFeedbackFailure())
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchSubmitFeedback() {
  yield takeLatest(FeedbackActionTypes.SubmitFeedback, submitFeedbackSaga)
}

export function* feedbackRootSaga() {
  yield fork(watchSubmitFeedback)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) => state.feedback

export const selectFeedbackText = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.feedbackText,
  )

export const selectFeedbackType = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.feedbackType,
  )

export const selectIsOpen = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isOpen,
  )

export const selectIsSaving = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isSaving,
  )
